import { useCallback, useEffect, useState } from 'react';
import {
  Building2, ChevronRight, ExternalLink, Loader2, MessageSquare,
  RefreshCw, Send, TicketCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const STATUS_STYLES = {
  open:        'bg-blue-500/10 text-blue-600 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  resolved:    'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  closed:      'bg-slate-500/10 text-slate-500 border-slate-400/20',
};
const STATUS_LABELS   = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const STATUS_OPTIONS  = ['open', 'in_progress', 'resolved', 'closed'];
const PRIORITY_STYLES = {
  low: 'bg-slate-500/10 text-slate-500', medium: 'bg-amber-500/10 text-amber-700',
  high: 'bg-orange-500/10 text-orange-700', urgent: 'bg-red-500/10 text-red-600',
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };
const CATEGORY_LABELS = {
  interview_access: 'Interview Access', technical_issue: 'Technical',
  scheduling: 'Scheduling', account: 'Account', billing: 'Billing', general: 'General',
};

/* ─── TicketDetailSheet ──────────────────────────────────────────────────────── */
function TicketDetailSheet({ ticketId, open, onClose, onUpdated }) {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try { const r = await apiClient.getSupportTicketById(ticketId); setTicket(r.data); }
    catch { toast.error('Could not load ticket.'); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSend = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await apiClient.addTicketMessage(ticket._id, { content: reply.trim() });
      setReply(''); await load(); toast.success('Reply sent.');
    } catch (err) { toast.error(err.message || 'Failed to send.'); }
    finally { setSending(false); }
  };

  const handleStatus = async (status) => {
    setUpdatingStatus(true);
    try {
      await apiClient.updateTicketStatus(ticket._id, status);
      await load(); onUpdated?.();
      toast.success(`Ticket marked as ${STATUS_LABELS[status]}.`);
    } catch (err) { toast.error(err.message || 'Failed to update.'); }
    finally { setUpdatingStatus(false); }
  };

  const currentUserId = user?._id;

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) { onClose(); setTicket(null); } }}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" side="right">
        {/* Header */}
        <SheetHeader className="shrink-0 border-b border-border px-4 py-3 space-y-2">
          {loading ? <Skeleton className="h-5 w-48" /> : ticket ? (
            <>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', STATUS_STYLES[ticket.status])}>
                    {STATUS_LABELS[ticket.status]}
                  </span>
                  <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', PRIORITY_STYLES[ticket.priority])}>
                    {PRIORITY_LABELS[ticket.priority]}
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono">{ticket.ticketNumber}</span>
                </div>
                <SheetTitle className="text-sm leading-snug">{ticket.subject}</SheetTitle>

                {/* Company context */}
                <div className="flex items-center gap-2 flex-wrap">
                  {ticket.raisedBy && (
                    <p className="text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {`${ticket.raisedBy.firstName || ''} ${ticket.raisedBy.lastName || ''}`.trim() || ticket.raisedBy.email}
                      </span>
                      {' · '}{ticket.companyId}
                    </p>
                  )}
                  <button
                    onClick={() => navigate(`/super-admin/companies/${ticket.companyId}`)}
                    className="inline-flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                  >
                    View Company <ExternalLink className="h-2.5 w-2.5" />
                  </button>
                </div>
              </div>

              {/* Category */}
              <p className="text-[11px] text-muted-foreground">
                Category: <span className="font-medium text-foreground">{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
              </p>

              {/* Status actions */}
              {ticket.status !== 'closed' && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {STATUS_OPTIONS.filter(s => s !== ticket.status).map(s => (
                    <button key={s} onClick={() => handleStatus(s)} disabled={updatingStatus}
                      className={cn('rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80', STATUS_STYLES[s])}>
                      {updatingStatus ? '…' : `Mark ${STATUS_LABELS[s]}`}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </SheetHeader>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!loading && ticket?.messages?.map((msg, i) => {
            const isOwn = msg.senderId === currentUserId || msg.senderId?._id === currentUserId;
            return (
              <div key={i} className={cn('flex items-end gap-2', isOwn ? 'flex-row-reverse' : 'flex-row')}>
                <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  isOwn ? 'bg-primary/15 text-primary' : 'bg-emerald-500/15 text-emerald-700')}>
                  {msg.senderName?.slice(0,2).toUpperCase() || '?'}
                </div>
                <div className={cn('max-w-[70%] space-y-1', isOwn && 'items-end flex flex-col')}>
                  {!isOwn && <p className="text-[10px] text-muted-foreground ml-1">{msg.senderName}</p>}
                  <div className={cn('rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                    isOwn ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm')}>
                    {msg.content}
                  </div>
                  <p className={cn('text-[10px] text-muted-foreground', isOwn ? 'text-right' : 'text-left')}>
                    {msg.createdAt ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply */}
        <div className="shrink-0 border-t border-border bg-card px-4 py-3">
          {ticket?.status === 'closed' ? (
            <p className="text-center text-xs text-muted-foreground py-1">This ticket is closed.</p>
          ) : (
            <div className="flex items-end gap-2">
              <textarea value={reply} onChange={e => setReply(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Reply to the company…"
                rows={2} disabled={sending}
                className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
              <Button size="icon" onClick={handleSend} disabled={!reply.trim() || sending} className="h-9 w-9 shrink-0 rounded-xl">
                {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── SuperAdminSupportPage ──────────────────────────────────────────────────── */
export default function SuperAdminSupportPage() {
  const [tickets,        setTickets]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [statusFilter,   setStatusFilter]   = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [stats,          setStats]          = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        apiClient.getSupportTickets({ type: 'company_to_superadmin', status: statusFilter || undefined }),
        apiClient.getSupportTicketStats(),
      ]);
      setTickets(tRes.data || []);
      setStats(sRes.data || {});
    } catch { toast.error('Could not load tickets.'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <TicketCheck className="h-5 w-5 text-primary" />
            Company Support Tickets
          </h1>
          <p className="text-sm text-muted-foreground">Tickets raised by company admins</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8">
          <RefreshCw className="h-3.5 w-3.5" />Refresh
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Open',        key: 'open',        color: 'text-blue-600'    },
          { label: 'In Progress', key: 'in_progress', color: 'text-amber-600'   },
          { label: 'Resolved',    key: 'resolved',    color: 'text-emerald-600' },
          { label: 'Closed',      key: 'closed',      color: 'text-slate-500'   },
        ].map(({ label, key, color }) => (
          <button key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={cn('rounded-xl border border-border bg-card p-4 text-left transition-all hover:shadow-sm',
              statusFilter === key && 'ring-2 ring-primary/40 border-primary/30')}>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold mt-0.5', color)}>{stats[key] ?? 0}</p>
          </button>
        ))}
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {['', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-all',
              statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-foreground/30')}>
            {s ? STATUS_LABELS[s] : 'All'}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-md" />)}</div>
      ) : tickets.length === 0 ? (
        <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
          <TicketCheck className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground">No tickets</p>
          <p className="text-xs text-muted-foreground mt-1">Company admins haven't raised any tickets yet.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
          {tickets.map(ticket => {
            const raisedBy = ticket.raisedBy;
            const name = raisedBy ? `${raisedBy.firstName || ''} ${raisedBy.lastName || ''}`.trim() || raisedBy.email : '—';
            return (
              <button key={ticket._id} onClick={() => setSelectedTicketId(ticket._id)}
                className="w-full text-left px-4 py-3.5 hover:bg-muted/40 transition-colors flex items-start gap-3 border-b border-border last:border-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 mt-0.5">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', STATUS_STYLES[ticket.status])}>
                      {STATUS_LABELS[ticket.status]}
                    </span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', PRIORITY_STYLES[ticket.priority])}>
                      {PRIORITY_LABELS[ticket.priority]}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-mono">{ticket.ticketNumber}</span>
                  </div>
                  <p className="text-sm font-medium text-foreground truncate">{ticket.subject}</p>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    <span className="font-medium">{ticket.companyId}</span>
                    {' · '}{name}
                    {' · '}{CATEGORY_LABELS[ticket.category] || ticket.category}
                    {' · '}{ticket.lastActivityAt ? new Date(ticket.lastActivityAt).toLocaleDateString() : ''}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2.5" />
              </button>
            );
          })}
        </div>
      )}

      {/* Detail sheet */}
      <TicketDetailSheet
        ticketId={selectedTicketId}
        open={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        onUpdated={load}
      />
    </div>
  );
}
